import axios from 'axios';
import DOMPurify from 'dompurify';
import Link from 'next/link';
import { Suspense } from 'react';
import React, { lazy, useCallback, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { lazily } from 'react-lazily';

import {
	GetProfileResponse,
	PutProfileRequest,
	putProfileResolverSchema,
} from 'services/account';
import { getAddress, getAddressList } from 'services/generic';

import { useBrowsing } from 'hooks/use-browsing';
import { useDebounce } from 'hooks/use-debounce';

import styles from './additionalinfo.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const InfoBlock = lazy(() => import('core/generic/elements/info-block'));
const { Insert, Select, DropdownList, Phone, Hint } = lazily(
	() => import('core/generic/elements/form')
);

// This will be replaced by the component type in the future.
export type Value = {
	value: string | number;
	text: string;
	group?: string;
};

type OptionProps = {
	value: Value[] | string | number;
	text: string;
	group?: string;
};

const mockOptions: OptionProps[] = [
	{ value: 1, text: 'Option 1' },
	{ value: 2, text: 'Option 2' },
	{ value: 3, text: 'Option 3' },
];

const data = {
	tabName: 'Register',
	pageDescription: 'Tell us more about yourself',
	skipButtonLabel: 'Skip this step',
	firstName: {
		label: 'First name (optional)',
		placeholder: 'First name',
		errorMessage: '',
		defaultValue: '',
	},
	lastName: {
		label: '',
	},
};

export const AdditionalInfoForm = () => {
	const {
		control,
		handleSubmit,
		watch,
		getValues,
		setValue,
		register,
		reset,
		trigger,
		formState: { dirtyFields, errors },
	} = useForm<any>({ mode: 'onBlur' });
	return (
		<Suspense fallback={null}>
			<form onSubmit={() => null} id="form-profile">
				<div className={styles['form-box']}>
					<div
						className={`display-medium bold ${styles.navigationButtons}`}
					>
						<a
							href="/register"
							className={`${styles.linkButton} ${styles.active}`}
						>
							{data.tabName}
						</a>
					</div>
					<div className={styles['form-box--content']}>
						<div className={styles.skipButtonWrapper}>
							<div className="body-medium bold">
								{data.pageDescription}
							</div>
							<Button
								type="button"
								label={data.skipButtonLabel}
								variant="tertiary"
								className={styles.skipButton}
								icon="ChevronRight"
								isNoPadding
							/>
						</div>
						<div className={styles.withHint}>
							<Insert
								defaultValue={data.firstName.defaultValue}
								tabIndex={-1}
								id="first-name"
								label={data.firstName.label}
								required={false}
								error={data.firstName.errorMessage}
								disabled={false}
								placeholder={data.firstName.placeholder}
							/>
							<div className={styles.hint}>
								<Hint />
							</div>
						</div>
						<div className={styles.withHint}>
							<Insert
								defaultValue={''}
								tabIndex={-1}
								id="last-name"
								label="Last name (optional)"
								required={false}
								error={''}
								disabled={false}
								placeholder="Last name"
							/>
							<div className={styles.hint}>
								<Hint />
							</div>
						</div>
						<Select
							{...register('country')}
							control={control}
							id="country"
							label="Country (optional)"
							error={''}
							placeholder="Select country"
							options={mockOptions}
						/>
						<Select
							{...register('gender')}
							control={control}
							id="gender"
							label="Gender (optional)"
							error={''}
							placeholder="Select gender"
							options={mockOptions}
						/>
						<Select
							{...register('ethnicity')}
							control={control}
							id="ethnicity"
							label="Ethnicity (optional)"
							error={''}
							placeholder="Select ethnicity"
							options={mockOptions}
						/>
						<Select
							{...register('lgbtq')}
							control={control}
							id="lgbtq"
							label="Would you describe yiurself as being in the LGBTQ+ community (optional)"
							error={''}
							placeholder="LGBTQ community"
							options={mockOptions}
						/>
						<Select
							{...register('favplayer')}
							control={control}
							id="favplayer"
							label="Favourite player (optional)"
							error={''}
							placeholder="Favourite player"
							options={mockOptions}
						/>
						<Button
							type="submit"
							label="Register"
							variant="primary"
							className={styles.submitButton}
						/>
					</div>
				</div>
			</form>
		</Suspense>
	);
};

export default AdditionalInfoForm;
